/**
 * Platform SMS — Templates & KPI (Lot 18 finalisation).
 *
 * Composants exportés :
 *   - <SmsTemplatesTab />     — édition des 12 templates avec preview temps réel
 *   - <SmsKpiTab />           — cards KPI (today/week/month, delivery rate, cost)
 *   - <SmsJournalFilters />   — filtres status + search pour le Journal
 */
import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import {
  Sparkles, Wand2, RotateCcw, Save, Loader2, Eye, MessageSquare,
  TrendingUp, AlertTriangle, CheckCircle2, Search, Filter,
} from "lucide-react";
import api from "@/lib/api";

// ────────────────────────────────────────────────────────────────────────
// SmsTemplatesTab
// ────────────────────────────────────────────────────────────────────────

export const SmsTemplatesTab = () => {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [supportedVars, setSupportedVars] = useState([]);
  const [unitCost, setUnitCost] = useState(0.065);
  const [selectedKey, setSelectedKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.platformListSmsTemplates();
      setTemplates(d.templates || []);
      setSupportedVars(d.supported_vars || []);
      setUnitCost(d.unit_cost_eur ?? 0.065);
      if (!selectedKey && d.templates?.[0]) setSelectedKey(d.templates[0].key);
    } finally { setLoading(false); }
  }, [selectedKey]);

  useEffect(() => { load(); }, []);

  const selected = useMemo(
    () => templates.find((t) => t.key === selectedKey),
    [templates, selectedKey]
  );

  if (loading) return <Loader2 size={16} className="animate-spin" />;

  return (
    <section className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6"
             data-testid="sms-templates-tab">
      {/* Left: list */}
      <div className="rounded-2xl border border-black/5 bg-white p-3">
        <p className="px-2 py-1 text-[10.5px] font-semibold uppercase tracking-widest text-[#6B7280]">
          12 modèles
        </p>
        <div className="mt-2 space-y-0.5" data-testid="templates-list">
          {templates.map((t) => (
            <button key={t.key}
                    onClick={() => setSelectedKey(t.key)}
                    data-testid={`tpl-btn-${t.key}`}
                    className={`w-full text-left px-3 py-2 rounded-xl transition-colors ${
                       selectedKey === t.key
                         ? "bg-[#F3FFB0] text-[#111]"
                         : "hover:bg-[#F7F8FA] text-[#374151]"
                     }`}>
              <p className="text-[12.5px] font-semibold">{t.label}</p>
              <p className="mt-0.5 text-[10.5px] text-[#6B7280] font-mono">{t.key}</p>
              {t.source !== "default" && (
                <span className="mt-1 inline-block text-[9.5px] font-semibold uppercase
                                bg-[#DBEAFE] text-[#1E40AF] px-1.5 py-0.5 rounded">
                  {t.source === "agency" ? "override agence" : "override plateforme"}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Right: editor + preview */}
      {selected && (
        <TemplateEditor
          key={selected.key}
          template={selected}
          supportedVars={supportedVars}
          onSaved={load}
        />
      )}
    </section>
  );
};

// ────────────────────────────────────────────────────────────────────────
// TemplateEditor
// ────────────────────────────────────────────────────────────────────────

const TemplateEditor = ({ template, supportedVars, onSaved }) => {
  const [body, setBody] = useState(template.body);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");

  // Real-time preview (debounced)
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const p = await api.platformSmsPreview(body);
        setPreview(p); setPreviewErr("");
      } catch (e) { setPreviewErr(e.message || "erreur"); setPreview(null); }
    }, 250);
    return () => clearTimeout(t);
  }, [body]);

  const dirty = body !== template.body;
  const textareaRef = useRef(null);

  const insertVar = (v) => {
    const el = textareaRef.current;
    if (!el) return setBody((b) => b + `{{${v}}}`);
    const start = el.selectionStart ?? body.length;
    const end   = el.selectionEnd ?? body.length;
    const token = `{{${v}}}`;
    const next  = body.slice(0, start) + token + body.slice(end);
    setBody(next);
    // Focus + place cursor after inserted token
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      await api.platformUpdateSmsTemplate(template.key, { body });
      await onSaved?.();
    } catch (e) { setError(e.message || "Erreur sauvegarde"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    setSaving(true); setError("");
    try {
      await api.platformResetSmsTemplate(template.key);
      await onSaved?.();
    } catch (e) { setError(e.message || "Erreur reset"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="template-editor">
      <div className="rounded-2xl border border-black/5 bg-white p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10.5px] font-semibold uppercase tracking-widest text-[#6B7280]">
              Modèle
            </p>
            <h3 className="mt-1 text-[18px] font-semibold text-[#111]">{template.label}</h3>
            <p className="mt-1 text-[11.5px] text-[#6B7280] font-mono">
              trigger : {template.trigger_event}
            </p>
          </div>
          {template.source !== "default" && (
            <button onClick={reset} disabled={saving}
                    data-testid="tpl-reset-btn"
                    className="h-9 rounded-full bg-white border border-black/10 px-3
                              text-[12px] font-medium text-[#6B7280] hover:text-[#111]
                              inline-flex items-center gap-1.5 disabled:opacity-50">
              <RotateCcw size={12} /> Réinitialiser
            </button>
          )}
        </div>

        <label className="block text-[11.5px] font-semibold text-[#374151] mb-1.5">
          Corps du message
        </label>
        <textarea data-testid="template-body-input"
                  ref={textareaRef}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  maxLength={1000}
                  rows={4}
                  className="w-full rounded-xl border border-black/10 px-3 py-2.5 text-[13.5px]
                            font-mono focus:outline-none focus:ring-2 focus:ring-[#D8FF2B]" />

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-widest text-[#6B7280]">
            Variables :
          </span>
          {supportedVars.map((v) => (
            <button key={v} type="button" onClick={() => insertVar(v)}
                    data-testid={`insert-var-${v}`}
                    className="h-7 rounded-full bg-[#F7F8FA] hover:bg-[#EEF2F5]
                              text-[11px] font-mono text-[#111] px-2.5">
              {`{{${v}}}`}
            </button>
          ))}
        </div>

        {error && (
          <div className="mt-3 rounded-lg bg-[#FEE2E2] text-[#991B1B] px-3 py-2 text-[12px]">
            {error}
          </div>
        )}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={() => setBody(template.body)}
                  disabled={!dirty || saving}
                  className="h-10 rounded-full bg-white border border-black/10 px-4
                            text-[12.5px] font-medium text-[#6B7280] disabled:opacity-50">
            Annuler
          </button>
          <button onClick={save} disabled={!dirty || saving}
                  data-testid="tpl-save-btn"
                  className="h-10 rounded-full bg-[#111] text-white px-5
                            text-[12.5px] font-semibold inline-flex items-center gap-2
                            disabled:opacity-50">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            Enregistrer
          </button>
        </div>
      </div>

      {/* Preview */}
      <div className="rounded-2xl border border-[#D8FF2B]/40 bg-[#FAFFF0] p-5"
           data-testid="template-preview">
        <div className="flex items-center gap-2 mb-3">
          <Eye size={13} strokeWidth={2} className="text-[#6B7280]" />
          <p className="text-[10.5px] font-semibold uppercase tracking-widest text-[#6B7280]">
            Aperçu · Ce qui sera envoyé
          </p>
        </div>
        {previewErr ? (
          <p className="text-[12.5px] text-[#991B1B]">{previewErr}</p>
        ) : preview ? (
          <>
            <div className="rounded-xl bg-white border border-black/5 p-4">
              <p className="text-[14px] font-mono text-[#111] whitespace-pre-wrap">
                {preview.rendered_body}
              </p>
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2 text-center">
              <PreviewMetric label="Caractères" value={preview.length} testId="preview-length" />
              <PreviewMetric label="Segments"   value={preview.segments} testId="preview-segments" />
              <PreviewMetric label="Encoding"   value={preview.encoding} testId="preview-encoding" small />
              <PreviewMetric label="Coût est."
                             value={`${preview.cost.amount.toFixed(3)} €`}
                             testId="preview-cost" small />
            </div>
            {preview.unknown_variables?.length > 0 && (
              <p className="mt-3 text-[11.5px] text-[#B45309]">
                ⚠ Variables inconnues : {preview.unknown_variables.join(", ")}
              </p>
            )}
          </>
        ) : (
          <Loader2 size={16} className="animate-spin text-[#6B7280]" />
        )}
      </div>
    </div>
  );
};

const PreviewMetric = ({ label, value, testId, small }) => (
  <div className="rounded-xl bg-white border border-black/5 py-3"
       data-testid={testId}>
    <p className={`${small ? "text-[13px]" : "text-[18px]"} font-semibold text-[#111] leading-none`}>
      {value}
    </p>
    <p className="mt-1 text-[9.5px] uppercase tracking-widest text-[#6B7280]">{label}</p>
  </div>
);


// ────────────────────────────────────────────────────────────────────────
// SmsKpiTab
// ────────────────────────────────────────────────────────────────────────

export const SmsKpiTab = () => {
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const k = await api.platformSmsKpi();
        setKpi(k);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <Loader2 size={18} className="animate-spin" />;
  if (!kpi) return <p className="text-[13px] text-[#6B7280]">Aucune donnée.</p>;

  return (
    <section className="space-y-6" data-testid="sms-kpi-tab">
      {/* 3 period cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <PeriodCard title="Aujourd'hui" data={kpi.today} testId="kpi-today" />
        <PeriodCard title="Cette semaine" data={kpi.week} testId="kpi-week" />
        <PeriodCard title="Ce mois" data={kpi.month} testId="kpi-month" />
      </div>

      {/* Bottom metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <MetricRow icon={CheckCircle2} iconColor="text-[#22C55E]"
                   label="Taux de délivrance (mois)"
                   value={kpi.month.delivery_rate !== null
                          ? `${kpi.month.delivery_rate.toFixed(1)} %` : "—"}
                   testId="metric-delivery-rate" />
        <MetricRow icon={AlertTriangle} iconColor="text-[#EF4444]"
                   label="Taux d'échec (mois)"
                   value={kpi.month.failure_rate !== null
                          ? `${kpi.month.failure_rate.toFixed(1)} %` : "—"}
                   testId="metric-failure-rate" />
        <MetricRow icon={TrendingUp} iconColor="text-[#111]"
                   label="Moyenne SMS / dossier (mois)"
                   value={kpi.avg_sms_per_dossier_this_month}
                   testId="metric-avg-per-dossier" />
      </div>

      <p className="text-[10.5px] text-[#6B7280]">
        Coût unitaire : {kpi.unit_cost_eur.toFixed(3)} € / segment ·
        Mise à jour : {new Date(kpi.as_of).toLocaleTimeString("fr-FR")}
      </p>
    </section>
  );
};

const PeriodCard = ({ title, data, testId }) => (
  <div className="rounded-2xl border border-black/5 bg-white p-5" data-testid={testId}>
    <p className="text-[10.5px] font-semibold uppercase tracking-widest text-[#6B7280]">
      {title}
    </p>
    <p className="mt-2 text-[36px] font-semibold text-[#111] leading-none">
      {data.count}
    </p>
    <p className="mt-1 text-[11.5px] text-[#6B7280]">SMS envoyés</p>
    <div className="mt-4 grid grid-cols-3 gap-1 text-center">
      <div>
        <p className="text-[13.5px] font-semibold text-[#166534]">{data.delivered}</p>
        <p className="text-[9.5px] uppercase tracking-widest text-[#6B7280]">Livrés</p>
      </div>
      <div>
        <p className="text-[13.5px] font-semibold text-[#B45309]">{data.pending || 0}</p>
        <p className="text-[9.5px] uppercase tracking-widest text-[#6B7280]">En cours</p>
      </div>
      <div>
        <p className="text-[13.5px] font-semibold text-[#991B1B]">{data.failed}</p>
        <p className="text-[9.5px] uppercase tracking-widest text-[#6B7280]">Échecs</p>
      </div>
    </div>
    <p className="mt-3 text-[11px] text-[#6B7280]">
      Coût : <b className="text-[#111]">{(data.cost?.amount || 0).toFixed(2)} €</b> · Segments : {data.segments}
    </p>
  </div>
);

const MetricRow = ({ icon: Icon, iconColor, label, value, testId }) => (
  <div className="rounded-2xl border border-black/5 bg-white p-4" data-testid={testId}>
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[#F7F8FA] flex items-center justify-center">
        <Icon size={16} strokeWidth={1.75} className={iconColor} />
      </div>
      <div>
        <p className="text-[10.5px] font-semibold uppercase tracking-widest text-[#6B7280]">
          {label}
        </p>
        <p className="mt-0.5 text-[19px] font-semibold text-[#111] leading-none">
          {value}
        </p>
      </div>
    </div>
  </div>
);


// ────────────────────────────────────────────────────────────────────────
// SmsJournalFilters (used on Journal tab)
// ────────────────────────────────────────────────────────────────────────

export const SmsJournalFilters = ({ status, q, onStatus, onQuery }) => {
  const badges = [
    { key: "",           label: "Tous",       cls: "" },
    { key: "delivered",  label: "Livrés",     cls: "bg-[#D9FFE8] text-[#166534]" },
    { key: "sent",       label: "Envoyés",    cls: "bg-[#DBEAFE] text-[#1D4ED8]" },
    { key: "pending",    label: "En attente", cls: "bg-[#F1F5F9] text-[#475569]" },
    { key: "queued",     label: "En file",    cls: "bg-[#DBEAFE] text-[#1E40AF]" },
    { key: "failed",     label: "Échecs",     cls: "bg-[#FEE2E2] text-[#991B1B]" },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="journal-filters">
      <Filter size={13} className="text-[#6B7280]" />
      {badges.map((b) => {
        const active = (status || "") === b.key;
        return (
          <button key={b.key || "all"}
                  onClick={() => onStatus(b.key)}
                  data-testid={`journal-status-${b.key || "all"}`}
                  className={`h-8 rounded-full px-3 text-[11.5px] font-medium transition-all ${
                     active ? "bg-[#111] text-white"
                            : b.cls || "bg-white border border-black/5 text-[#6B7280]"
                   }`}>
            {b.label}
          </button>
        );
      })}
      <div className="ml-auto relative">
        <Search size={12} className="absolute left-3 top-2.5 text-[#6B7280]" />
        <input type="text" value={q || ""} onChange={(e) => onQuery(e.target.value)}
               placeholder="Rechercher client, véhicule, téléphone..."
               data-testid="journal-search-input"
               className="h-8 pl-8 pr-3 rounded-full bg-white border border-black/5
                          text-[11.5px] w-[240px] focus:outline-none focus:ring-2 focus:ring-[#D8FF2B]" />
      </div>
    </div>
  );
};
